import VolumeOffIcon from '@mui/icons-material/VolumeOff';
import VolumeUpIcon from '@mui/icons-material/VolumeUp';
import {Box, Card, CardContent, Slider, Stack, Typography} from '@mui/material';
import {useCallback, useEffect, useRef, useState} from 'react';
import {User, UserSoundType} from '@cp/model';
import useSetSoundFile from '../../../hooks/users/useSetSoundFile';
import useSetSoundVolume from '../../../hooks/users/useSetSoundVolume';
import GeneralSettingsSoundField from './GeneralSettingsSoundField';

export interface GeneralSettingsSoundProps {
  user?: User | null;
}

export default function GeneralSettingsSound({user}: GeneralSettingsSoundProps) {
  const {setSoundFile, loading: fileLoading} = useSetSoundFile();
  const {setSoundVolume, loading: volumeLoading} = useSetSoundVolume();

  const soundVolume = user?.soundVolume;
  const [localVolume, setLocalVolume] = useState(soundVolume || 0);
  const debounceTimeoutRef = useRef<NodeJS.Timeout | null>(null);

  const loading = fileLoading || volumeLoading;

  // Update local volume when user data changes
  useEffect(() => {
    setLocalVolume(soundVolume || 0);
  }, [soundVolume]);

  // Debounced volume update function
  const debouncedSetVolume = useCallback(
    (volume: number) => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }

      debounceTimeoutRef.current = setTimeout(() => {
        setSoundVolume(volume);
      }, 3000); // 3 second delay
    },
    [setSoundVolume],
  );

  // Cleanup timeout on unmount
  useEffect(
    () => () => {
      if (debounceTimeoutRef.current) {
        clearTimeout(debounceTimeoutRef.current);
      }
    },
    [],
  );

  const handleVolumeChange = (_: Event, value: number | number[]) => {
    const volume = Array.isArray(value) ? value[0] : value;
    setLocalVolume(volume); // Update local state immediately
    debouncedSetVolume(volume); // Debounced backend update
  };

  const handleFileSelect = async (soundType: UserSoundType, file: File | null) => {
    await setSoundFile(soundType, file);
  };

  return (
    <Box sx={{mt: 3}}>
      <Box sx={{display: 'flex', alignItems: 'center', gap: 2, mb: 3}}>
        {localVolume === 0 ? <VolumeOffIcon color="disabled" /> : <VolumeUpIcon color="primary" />}
        <Typography variant="h5">Sound Settings</Typography>
      </Box>

      {/* Volume Control */}
      <Card sx={{mb: 4}}>
        <CardContent>
          <Typography variant="h6" sx={{mb: 2}}>
            Global Volume
          </Typography>
          <Typography variant="body2" color="text.secondary" sx={{mb: 1}}>
            Volume: {localVolume}%
          </Typography>
          <Slider
            value={localVolume}
            onChange={handleVolumeChange}
            min={0}
            max={100}
            step={5}
            disabled={loading}
            sx={{maxWidth: 400}}
            marks={[
              {value: 0, label: '0%'},
              {value: 25, label: '25%'},
              {value: 50, label: '50%'},
              {value: 75, label: '75%'},
              {value: 100, label: '100%'},
            ]}
          />
          <Typography variant="caption" color="text.secondary" sx={{mt: 1, display: 'block'}}>
            Changes will be saved automatically after 3 seconds of inactivity.
          </Typography>
        </CardContent>
      </Card>

      {/* Individual Sound Fields */}
      <Typography variant="h6" sx={{mb: 2}}>
        Custom Sound Files
      </Typography>
      <Typography variant="body2" color="text.secondary" sx={{mb: 3}}>
        Upload custom sound files for different UI interactions. Drag and drop files or click to select.
      </Typography>
      <Stack direction="row" gap={2} flexWrap="wrap">
        <GeneralSettingsSoundField
          soundType={UserSoundType.CLICK}
          soundUrl={user?.clickSoundUrl}
          onFileSelect={handleFileSelect}
          loading={loading}
        />

        <GeneralSettingsSoundField
          soundType={UserSoundType.CHECK}
          soundUrl={user?.checkSoundUrl}
          onFileSelect={handleFileSelect}
          loading={loading}
        />

        <GeneralSettingsSoundField
          soundType={UserSoundType.DROPDOWN}
          soundUrl={user?.dropdownSoundUrl}
          onFileSelect={handleFileSelect}
          loading={loading}
        />

        <GeneralSettingsSoundField
          soundType={UserSoundType.SWITCH}
          soundUrl={user?.switchSoundUrl}
          onFileSelect={handleFileSelect}
          loading={loading}
        />

        <GeneralSettingsSoundField
          soundType={UserSoundType.SLIDE}
          soundUrl={user?.slideSoundUrl}
          onFileSelect={handleFileSelect}
          loading={loading}
        />
      </Stack>
    </Box>
  );
}
